'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@medizzconnect/shared';
import { useFilterStore } from './filter-store';
import { useSearch } from '../search/use-search';
import type { Database } from '@medizzconnect/db';

type MedicalCategoryRow = Database['public']['Tables']['medical_categories']['Row'];

interface ActiveFiltersProps {
  categories: MedicalCategoryRow[];
  className?: string;
}

/**
 * Zeigt aktive Filter als Badges an.
 * Suchbegriff (aus URL) + Kategorie + Subcategory + Treatment.
 * Erscheint nur, wenn mindestens ein Filter aktiv ist.
 */
export function ActiveFilters({ categories, className }: ActiveFiltersProps) {
  const t = useTranslations('home');
  const { categoryId, subcategoryName, treatmentName, setCategoryId, setSubcategoryName, setTreatmentName, resetFilters } =
    useFilterStore();
  const { query, clearSearch } = useSearch();

  const activeCategory = categories.find((c) => c.id === categoryId);
  const hasActiveFilters = !!(query || categoryId || subcategoryName || treatmentName);

  // Übersetzten Anzeigenamen für Subcategory-DE-Key finden
  const findSubcategoryDisplayName = (deKey: string): string => {
    for (const cat of categories) {
      const subs = (cat.subcategories as unknown as Array<{ name: string; name_original?: string }>) ?? [];
      if (!Array.isArray(subs)) continue;
      for (const sub of subs) {
        if ((sub.name_original ?? sub.name) === deKey) return sub.name;
      }
    }
    return deKey; // Fallback: DE-Key direkt anzeigen
  };

  // Übersetzten Anzeigenamen für Treatment-DE-Key finden
  const findTreatmentDisplayName = (deKey: string): string => {
    for (const cat of categories) {
      const subs = (cat.subcategories as unknown as Array<{ treatments: string[]; treatments_original?: string[] }>) ?? [];
      if (!Array.isArray(subs)) continue;
      for (const sub of subs) {
        const origList = sub.treatments_original ?? sub.treatments ?? [];
        const idx = origList.indexOf(deKey);
        if (idx !== -1 && sub.treatments?.[idx] !== undefined) return sub.treatments[idx];
      }
    }
    return deKey; // Fallback: DE-Key direkt anzeigen
  };

  if (!hasActiveFilters) return null;

  const handleResetAll = () => {
    resetFilters();
    clearSearch();
  };

  return (
    <div className={cn('py-3', className)}>
      <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-text-muted">{t('filters.active')}:</span>

          {/* Suchbegriff-Badge */}
          {query && (
            <Badge label={`"${query}"`} onRemove={clearSearch} />
          )}

          {/* Kategorie-Badge */}
          {activeCategory && (
            <Badge
              label={activeCategory.name}
              color={activeCategory.color ?? undefined}
              onRemove={() => setCategoryId(null)}
            />
          )}

          {/* Subcategory-Badge */}
          {subcategoryName && (
            <Badge label={findSubcategoryDisplayName(subcategoryName)} onRemove={() => setSubcategoryName(null)} />
          )}

          {/* Treatment-Badge */}
          {treatmentName && (
            <Badge label={findTreatmentDisplayName(treatmentName)} onRemove={() => setTreatmentName(null)} />
          )}

          {/* Reset-Button */}
          <button
            type="button"
            onClick={handleResetAll}
            className="ml-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            {t('filters.resetAll')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Badge Sub-Component ── */

interface BadgeProps {
  label: string;
  color?: string;
  onRemove: () => void;
}

function Badge({ label, color, onRemove }: BadgeProps) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-bg-muted text-text-secondary transition-colors"
      style={color ? { backgroundColor: `${color}20`, color } : undefined}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-black/10 transition-colors"
        aria-label={`${label} entfernen`}
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
}
